import { AuthProvider, useAuth } from './modules/accounts/index.js'
import { FacilityProvider, useFacilityContext } from './modules/facilities/index.js'
import { useFacility } from './modules/facilities/index.js'
import { useMe } from './modules/accounts/index.js'
import { LoadingWindowProvider, ReadingBar, Skeleton, isNotFoundError, type SessionRole } from './modules/core/index.js'
import { BrandLogo } from './modules/core/index.js'
import { UserMenu } from './modules/accounts/index.js'
import { FacilityList } from './modules/facilities/index.js'
import { Link, NavIcon, navigate, PageWidthButton, usePathname, useRememberedTab, type NavSection } from './modules/core/index.js'
import { Login } from './modules/accounts/index.js'
import { AcceptInvitation, ForgotPassword, InviteOperatorModal, ResetPassword, StaffNotificationsModal, useInviteOperator } from './modules/accounts/index.js'
import { Dashboard, DASHBOARD_TAB_LABELS, DASHBOARD_TAB_STORAGE_KEY, DASHBOARD_TABS, type DashboardTab } from './pages/Dashboard.js'
import { useDecisions } from './modules/schedule/index.js'
import { useFacilityBarriers } from './modules/messages/index.js'
import { Configuration } from './modules/facilities/index.js'
import { Reporting } from './pages/Reporting.js'
import { Tools } from './pages/Tools.js'
import { CountBadge } from './modules/core/index.js'
import { ArrowLeft, MailPlus } from 'lucide-react'
import { FacilityAdministratorIcon, FacilityIcon, OperatorIcon } from './modules/core/index.js'
import { DemoSessionBanner } from './modules/accounts/index.js'
import { ViewAsMenu } from './modules/accounts/index.js'
import { useState } from 'react'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { NewFacilityModal } from './modules/facilities/index.js'
import { FeedbackButton } from './modules/feedback/index.js'
import { operatorFeedbackApi } from './modules/feedback/index.js'
import { featureWithPage, featureWithSegment, useFeatures, type FeatureWebModule } from './modules/core/index.js'
import { FeatureGate } from './modules/add-ons/index.js'
import { useFeature } from './modules/add-ons/index.js'
import { Button } from './modules/core/index.js'
import { HelpSeatProvider } from './modules/core/index.js'
import { LoadFailure } from './modules/core/index.js'

/** One of the Dashboard's sections on the main bar (the mockup's flat navigation): on the Dashboard it switches the section, elsewhere it opens the Dashboard on it. */
function SectionLink({ href, icon, active, onDashboard, onChoose, children, count = 0, countLabel }: { href: string; icon: NavSection; active: boolean; onDashboard: boolean; onChoose: () => void; children: string; count?: number; countLabel?: string }) {
  return (
    <a
      href={href}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) {
          return
        }
        event.preventDefault()
        onChoose()
        if (!onDashboard) {
          navigate(href.split('?')[0] ?? '/')
        }
      }}
      className={`relative inline-flex items-center gap-1.5 text-xs font-bold px-3 py-3 border-b-2 ${active ? 'text-marine border-coral' : 'text-ink-mid border-transparent'}`}
    >
      <NavIcon section={icon} />
      {children}
      <span className="absolute top-2.5 right-0.5">
        <span className="relative block">
          <CountBadge count={count} label={countLabel} />
        </span>
      </span>
    </a>
  )
}

function NavLink({ to, icon, active, children, count = 0, countLabel }: { to: string; icon: NavSection; active: boolean; children: string; count?: number; countLabel?: string }) {
  return (
    <Link
      to={to}
      className={`relative inline-flex items-center gap-1.5 text-xs font-bold px-3 py-3 border-b-2 ${active ? 'text-marine border-coral' : 'text-ink-mid border-transparent'}`}
    >
      <NavIcon section={icon} />
      {children}
      {/* The same badge the admin's Feedback tab wears: how many are waiting, on the tab's corner. */}
      <span className="absolute top-2.5 right-0.5">
        <span className="relative block">
          <CountBadge count={count} label={countLabel} />
        </span>
      </span>
    </Link>
  )
}

/** The shell's own pages, or an add-on's tab by its URL segment (src/features/index.ts). */
type FacilityPage = 'dashboard' | 'config' | 'tools' | 'reporting' | { feature: FeatureWebModule }

interface FacilityRoute {
  facilityId: string
  page: FacilityPage
}

const PAGE_OF_SEGMENT: Record<string, FacilityPage> = { config: 'config', tools: 'tools', reporting: 'reporting' }

/** The page a URL segment names: one of the shell's, an add-on's tab, or the dashboard for none. */
function pageOfSegment(features: readonly FeatureWebModule[], segment: string | undefined): FacilityPage {
  if (!segment) {
    return 'dashboard'
  }
  const feature = featureWithSegment(features, segment)
  return PAGE_OF_SEGMENT[segment] ?? (feature ? { feature } : 'dashboard')
}

/**
 * Only matches the multi-facility URL scheme (/facilities/:id[/config|/tools|/reporting|/<add-on>]).
 * A client with exactly one facility keeps using the plain `/`, `/config`,
 * `/tools` and `/reporting` paths (handled by the hasMultipleFacilities fallback below)
 * - no URL change, no breadcrumb, nothing to navigate back to.
 */
function parseFacilityRoute(features: readonly FeatureWebModule[], pathname: string): FacilityRoute | null {
  const match = /^\/facilities\/([^/]+)(?:\/([^/]+))?\/?$/.exec(pathname)
  if (!match?.[1]) {
    return null
  }
  return { facilityId: decodeURIComponent(match[1]), page: pageOfSegment(features, match[2]) }
}

/** The single-facility paths: `/config`, `/tools`, `/reporting`, or an add-on's `/<segment>`. */
function pageOfPlainPath(features: readonly FeatureWebModule[], pathname: string): FacilityPage {
  return pageOfSegment(features, /^\/([^/]+)\/?$/.exec(pathname)?.[1])
}

/**
 * An add-on's tab: hidden until the client is licensed for it, marked Off
 * while this facility has it switched off, so an operator discovers what
 * the agreement includes (docs/plans/feature-licensing-plan.md section 4).
 */
function FeatureNavLink({ feature, facilityId, href, active }: { feature: FeatureWebModule; facilityId: string | null; href: string; active: boolean }) {
  const standing = useFeature(facilityId, feature.key)
  if (!standing.licensed || !feature.operatorTab) {
    return null
  }
  return (
    <NavLink to={href} icon="addOn" active={active}>
      {standing.enabled ? feature.operatorTab.label : `${feature.operatorTab.label} · Off`}
    </NavLink>
  )
}

/** An add-on's client-level page (Executive Insights): listed while the client is licensed, whatever facility is open. */
function ClientPageNavLink({ feature, active }: { feature: FeatureWebModule; active: boolean }) {
  const standing = useFeature(null, feature.key)
  if (!standing.licensed || !feature.operatorPage) {
    return null
  }
  return (
    <NavLink to={`/${feature.operatorPage.segment}`} icon={feature.operatorPage.icon} active={active}>
      {feature.operatorPage.label}
    </NavLink>
  )
}

function AuthenticatedShell({
  username,
  role,
  logout,
  pathname,
}: {
  username: string
  role: SessionRole
  logout: () => void
  pathname: string
}) {
  const { total: facilityCount, soleFacility, isLoading: isLoadingOverview, isError: isOverviewError } = useFacilityContext()
  // The facility overview lives in a provider with no refetch of its own: a retry asks every active query again.
  const queryClient = useQueryClient()
  const reading = useIsFetching() > 0
  const meQuery = useMe()
  // An operator invites a fellow operator (invitations plan D4) from the header.
  const [isInvitingOperator, setIsInvitingOperator] = useState(false)
  // The signed-in staff member's own notification settings (facility operations dashboard plan §5).
  const [isEditingNotifications, setIsEditingNotifications] = useState(false)
  const inviteOperator = useInviteOperator(meQuery.data?.clientId)
  const canManageAdministrators = role === 'operator'
  // A facility is the operator's record to create; SeeWhoCares's admin area only looks at it.
  const canCreateFacility = canManageAdministrators
  const [isCreatingFacility, setIsCreatingFacility] = useState(false)
  const features = useFeatures()

  const route = parseFacilityRoute(features, pathname)
  // A page of the client's rather than a facility's (an add-on's `operatorPage`), at `/<segment>` whatever facility is open.
  const clientPage = route ? null : featureWithPage(features, /^\/([^/]+)\/?$/.exec(pathname)?.[1])
  const hasMultipleFacilities = facilityCount > 1
  const activeFacilityId = route?.facilityId ?? soleFacility?.facilityId ?? null
  // The facility showing is read by id, never looked up in a list of them all; the sole facility already came with the overview.
  const isSoleFacility = soleFacility !== null && soleFacility.facilityId === activeFacilityId
  const facilityQuery = useFacility(isSoleFacility ? null : activeFacilityId)
  const activeFacility = isSoleFacility ? soleFacility : (facilityQuery.data ?? null)
  const isLoading = isLoadingOverview || (!isSoleFacility && activeFacilityId !== null && facilityQuery.isPending)
  // A facility that is not there is the "couldn't be found" message below, not a failure to load.
  const isError = isOverviewError || (facilityQuery.isError && !isNotFoundError(facilityQuery.error))
  const page: FacilityPage = clientPage ? 'dashboard' : (route?.page ?? pageOfPlainPath(features, pathname))
  const isConfigRoute = page === 'config'
  const isReportingRoute = page === 'reporting'
  const isToolsRoute = page === 'tools'
  const featurePage = typeof page === 'object' ? page.feature : null
  const showList = hasMultipleFacilities && !route && !clientPage
  // On a client page a multi-facility client has no facility open, so the list's bar shows rather than a facility's.
  const onClientPageOfMany = clientPage !== null && hasMultipleFacilities
  // An operator works across facilities, so from the list they can open the
  // configuration at its master, with no facility chosen yet. A facility
  // administrator configures a facility from that facility's own page.
  const canConfigureFromList = showList && canManageAdministrators
  const isListConfigRoute = canConfigureFromList && pathname.replace(/\/$/, '') === '/config'

  const dashboardHref = hasMultipleFacilities && activeFacilityId ? `/facilities/${encodeURIComponent(activeFacilityId)}` : '/'
  const configHref = hasMultipleFacilities && activeFacilityId ? `/facilities/${encodeURIComponent(activeFacilityId)}/config` : '/config'
  const reportingHref = hasMultipleFacilities && activeFacilityId ? `/facilities/${encodeURIComponent(activeFacilityId)}/reporting` : '/reporting'
  const toolsHref = hasMultipleFacilities && activeFacilityId ? `/facilities/${encodeURIComponent(activeFacilityId)}/tools` : '/tools'
  // The Dashboard's sections are the main bar's first items (facility operations dashboard plan §3, as mocked): Today wears
  // what needs a person, the Roster the notes nobody has marked seen.
  // A facility administrator always opens on Today, whatever this browser last chose (the user's rule, 26 October 2026).
  const dashboardTab = useRememberedTab<DashboardTab>(DASHBOARD_TAB_STORAGE_KEY, DASHBOARD_TABS, { openOnFirst: role === 'facility_administrator' })
  const needsYou = useDecisions(!showList && activeFacilityId ? activeFacilityId : null).data?.counts.total ?? 0
  const unseenNotes = (useFacilityBarriers(!showList && activeFacilityId ? activeFacilityId : null).data ?? []).filter((barrier) => barrier.acknowledgement === null).length
  const segmentHref = (segment: string) => (hasMultipleFacilities && activeFacilityId ? `/facilities/${encodeURIComponent(activeFacilityId)}/${segment}` : `/${segment}`)

  return (
    <div>
      <header className="bg-marine-dk px-7 py-2">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <BrandLogo className="h-6" />
            <span aria-hidden="true" className="h-5 w-px bg-white/20" />
            <div className="truncate text-sm font-black text-white">{clientPage ? clientPage.name : isListConfigRoute ? 'Configuration' : showList ? 'All Facilities' : (activeFacility?.name ?? 'Dashboard')}</div>
          </div>
          <div className="flex shrink-0 items-center gap-3 whitespace-nowrap">
            {canCreateFacility && !isLoading && !isError && (
              <Button icon={FacilityIcon} size="sm" onClick={() => setIsCreatingFacility(true)}>
                New facility
              </Button>
            )}
            {role === 'operator' && (
              <Button icon={MailPlus} size="sm" variant="secondary" onClick={() => setIsInvitingOperator(true)}>
                Invite an operator
              </Button>
            )}
            {/* An add-on's header action (the AI briefing's sparkle): the facility open, or null on the list and a client page. */}
            {features.map((feature) => (feature.headerAction ? <feature.headerAction key={`${feature.key}-action`} facilityId={showList || clientPage ? null : (activeFacilityId ?? null)} onClientPage={clientPage !== null} role={role} /> : null))}
            <ViewAsMenu role={role} />
            {/* The job title and the person's own notifications live in the panel the name opens, so the header stays one short row. */}
            <UserMenu
              username={username}
              firstName={meQuery.data?.firstName}
              lastName={meQuery.data?.lastName}
              organizationName={meQuery.data?.organizationName}
              role={
                canManageAdministrators
                  ? { label: 'Operator', icon: <OperatorIcon className="h-3.5 w-3.5" /> }
                  : { label: 'Facility administrator', icon: <FacilityAdministratorIcon className="h-3.5 w-3.5" /> }
              }
              onOpenNotifications={() => setIsEditingNotifications(true)}
              onSignOut={logout}
            />
          </div>
        </div>
      </header>
      {isEditingNotifications && <StaffNotificationsModal onClose={() => setIsEditingNotifications(false)} />}
      <DemoSessionBanner />
      {isInvitingOperator && <InviteOperatorModal organizationName={meQuery.data?.organizationName ?? null} invite={(input) => inviteOperator.mutateAsync(input)} onClose={() => setIsInvitingOperator(false)} />}

      {!showList && activeFacility && hasMultipleFacilities && (
        <div className="bg-marine-lt/30 border-b border-marine/15 px-7 py-2">
          <div className="max-w-5xl mx-auto">
            <Link to="/" className="text-[11px] font-bold text-marine hover:text-marine-dk inline-flex items-center gap-1">
              <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" /> All Facilities
            </Link>
          </div>
        </div>
      )}

      {/* The facilities list's bar: the list, an operator's master Configuration, and any page of the client's (Insights). A facility administrator with several facilities gets it too, for those pages. */}
      {((showList && !isListConfigRoute) || onClientPageOfMany) && (
        <nav className="bg-white border-b border-marine/15">
          <div className="max-w-5xl mx-auto flex gap-1 px-7">
            <NavLink to="/" icon="facilities" active={!isListConfigRoute && !clientPage}>
              Facilities
            </NavLink>
            {canManageAdministrators && (
              <NavLink to="/config" icon="configuration" active={isListConfigRoute}>
                Configuration
              </NavLink>
            )}
            {features.map((feature) => (feature.operatorPage ? <ClientPageNavLink key={feature.key} feature={feature} active={clientPage?.key === feature.key} /> : null))}
            <span className="flex-1" />
            <PageWidthButton />
          </div>
        </nav>
      )}

      {/* The Configuration brings its own bar of tabs, with a way back, in place of this one (mockup screen 3). */}
      {!showList && !isConfigRoute && !onClientPageOfMany && (
        <nav className="bg-white border-b border-marine/15">
          <div className="max-w-5xl mx-auto flex gap-1 px-7">
            {DASHBOARD_TABS.map((id) => (
              <SectionLink
                key={id}
                icon={id}
                href={`${dashboardHref}?tab=${id}`}
                active={page === 'dashboard' && !clientPage && dashboardTab.current === id}
                onDashboard={page === 'dashboard' && !clientPage}
                onChoose={() => dashboardTab.choose(id)}
                count={id === 'today' ? needsYou : id === 'roster' ? unseenNotes : 0}
                countLabel={id === 'today' ? 'things need you' : id === 'roster' ? 'notes not yet seen' : undefined}
              >
                {DASHBOARD_TAB_LABELS[id]}
              </SectionLink>
            ))}
            <NavLink to={reportingHref} icon="reporting" active={isReportingRoute && !clientPage}>
              Reporting
            </NavLink>
            {features.map((feature) => (feature.operatorPage ? <ClientPageNavLink key={`${feature.key}-page`} feature={feature} active={clientPage?.key === feature.key} /> : null))}
            {features.map((feature) =>
              feature.operatorTab ? <FeatureNavLink key={feature.key} feature={feature} facilityId={activeFacility?.facilityId ?? null} href={segmentHref(feature.operatorTab.segment)} active={featurePage?.key === feature.key} /> : null,
            )}
            <span className="flex-1" />
            <NavLink to={toolsHref} icon="tools" active={isToolsRoute}>
              Tools
            </NavLink>
            <NavLink to={configHref} icon="configuration" active={isConfigRoute}>
              Configuration
            </NavLink>
            <PageWidthButton />
          </div>
        </nav>
      )}

      <ReadingBar reading={reading} />

      {isLoading ? (
        <Skeleton lines={5} label="Loading the facilities" className="max-w-5xl mx-auto p-8" />
      ) : isError ? (
        <LoadFailure className="p-8" message="Failed to load facilities. Confirm the API is reachable." onRetry={() => void queryClient.refetchQueries({ type: 'active' })} />
      ) : clientPage?.operatorPage ? (
        <clientPage.operatorPage.page role={role} />
      ) : facilityCount === 0 ? (
        <div className="p-8 text-sm text-ink-mid max-w-5xl mx-auto">
          <p>
            <span className="font-bold text-ink">No facilities set up.</span>{' '}
            {canCreateFacility
              ? 'Register your first facility to get its dashboard and configuration here. Workers, administrators and shifts all belong to a facility.'
              : 'Your operator has not registered a facility yet. Once one is created, its dashboard and configuration will appear here.'}
          </p>
          {canCreateFacility && (
            <Button icon={FacilityIcon} className="mt-4" onClick={() => setIsCreatingFacility(true)}>
              Create the first facility
            </Button>
          )}
        </div>
      ) : isListConfigRoute ? (
        <Configuration facilityId={null} hasFacilityRoutes={hasMultipleFacilities} canManageAdministrators={canManageAdministrators} back={{ href: '/', label: 'Facilities' }} />
      ) : showList ? (
        <FacilityList total={facilityCount} clientId={meQuery.data?.clientId} canManageAdministrators={canManageAdministrators} />
      ) : !activeFacility ? (
        <div className="p-8 text-ink-mid text-sm">
          That facility couldn't be found.{' '}
          <Link to="/" className="font-bold text-marine hover:text-marine-dk">
            Back to all facilities
          </Link>
        </div>
      ) : featurePage?.operatorTab ? (
        <FeatureGate feature={featurePage.key} name={featurePage.name} facilityId={activeFacility.facilityId} role={role} addOnsHref={configHref}>
          <featurePage.operatorTab.page facilityId={activeFacility.facilityId} />
        </FeatureGate>
      ) : isToolsRoute ? (
        <Tools facilityId={activeFacility.facilityId} facilityCount={facilityCount} showWorkerSearch={role === 'operator'} />
      ) : isReportingRoute ? (
        <Reporting facilityId={activeFacility.facilityId} role={role} configHref={configHref} />
      ) : isConfigRoute ? (
        <Configuration facilityId={activeFacility.facilityId} hasFacilityRoutes={hasMultipleFacilities} canManageAdministrators={canManageAdministrators} back={{ href: dashboardHref, label: 'Dashboard' }} />
      ) : (
        <Dashboard key={activeFacility.facilityId} facilityId={activeFacility.facilityId} facilityName={activeFacility.name} clientId={meQuery.data?.clientId} canManageRoster={canManageAdministrators} canEditWorkers configHref={configHref} toolsHref={toolsHref} current={dashboardTab.current} choose={dashboardTab.choose} />
      )}

      {isCreatingFacility && (
        <NewFacilityModal clientId={meQuery.data?.clientId} organizationName={meQuery.data?.organizationName} onClose={() => setIsCreatingFacility(false)} />
      )}

      <FeedbackButton
        api={operatorFeedbackApi}
        details={{
          role: canManageAdministrators ? 'operator' : 'facility administrator',
          organization: meQuery.data?.organizationName,
          facilityId: activeFacility?.facilityId,
          facility: activeFacility?.name,
          view: clientPage ? clientPage.name : isConfigRoute || isListConfigRoute ? 'Configuration' : showList ? 'All facilities' : isToolsRoute ? 'Tools' : isReportingRoute ? 'Reporting' : featurePage ? featurePage.name : 'Dashboard',
        }}
      />
    </div>
  )
}

function AppShell() {
  const { isAuthenticated, username, role, logout } = useAuth()
  const pathname = usePathname()

  // Must come before the auth check - this is how a locked-out person
  // reaches the reset-password flow in the first place.
  if (pathname === '/reset-password') {
    return <ResetPassword />
  }
  if (pathname === '/forgot-password') {
    return <ForgotPassword />
  }
  // The invitation's accept page (invitations plan 3.4), reached with no session, like the reset.
  if (pathname === '/accept-invitation') {
    return <AcceptInvitation />
  }

  if (!isAuthenticated || !role) {
    return <Login />
  }

  // Signed in here with a worker's own login: that session's shell is the
  // worker app, which main.tsx picks on the next load.
  if (role === 'worker') {
    window.location.replace('/worker')
    return null
  }

  return (
    <FacilityProvider>
      <HelpSeatProvider seat={role === 'facility_administrator' ? 'facility_administrator' : 'operator'}>
        <LoadingWindowProvider>
          <AuthenticatedShell username={username ?? 'Account'} role={role} logout={logout} pathname={pathname} />
        </LoadingWindowProvider>
      </HelpSeatProvider>
    </FacilityProvider>
  )
}

export default function OperatorApp() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  )
}
